import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Form,
  Input,
  notification,
  Popconfirm,
  Space,
  Table,
  Typography,
  Row,
  Skeleton,
  Col,
  Card,
  Layout,
  List,
  Segmented,
  Tabs,
  Switch,
  Transfer,
  Collapse,
  Alert,
} from 'antd';
import dayjs from 'dayjs';
import CustomButton from '../../../components/CustomButton';

import { catchExceptionSRate, useQuery } from '../../../utils/helpers/helpers';
import ProjectForm from '../../../containers/ProjectForm/index';
import SpinLoad from '../../../components/SpinLoad';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import MESSAGES from '../../../enums/Messages';

import { getAll as getAllOrganizations } from '../../../api/module/organizations';
import { getAll as getAllClients } from '../../../api/module/clients';

import {
  getByProject as getAllProjectCategory,
  remove,
  post as postAllProjectCategory,
  put as putAllProjectCategory,
} from '../../../api/module/project_category';
import {
  post as postProject,
  put as putProject,
  get as getProject,
  getTemplates,
  setTemplates,
  info as projectInfo,
} from '../../../api/module/projects';

import './style.scss';

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;

const ProjectContractTypeTemplateInput = ({ project_id, params }) => {
  const [selected, setSelected] = useState([]);
  const [dataSource, setDataSource] = useState(undefined);
  const [required, setRequired] = useState({});

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        setDataSource(undefined);
        setSelected([]);
        const response = await getTemplates(project_id, params);
        setDataSource(response.data.results);
        setSelected(
          response.data.results
            .filter((s) => !!s.selected || s.obligatorio)
            .map((s) => s.value),
        );
        setRequired(
          response.data.results
            .filter((s) => !!s.selected || s.obligatorio)
            .reduce((pv, cv) => {
              pv[cv.value] = cv.requerido || cv.obligatorio;
              return pv;
            }, {}),
        );
      } catch (error) {
        catchExceptionSRate(error);
      }
    }
    fetchData();
  }, [JSON.stringify(params)]);

  const onSwitch = (i, value) => {
    setRequired((s) => ({ ...s, [i]: value }));
  };

  const columns = [
    {
      dataIndex: 'label',
      title: 'Nombre de plantilla a seleccionar',
    },
    {
      dataIndex: 'categoria_nombre',
      title: 'Categoría',
    },
  ];

  const columnsRight = [
    {
      dataIndex: 'label',
      title: 'Nombre de plantilla a visualizar',
    },
    {
      dataIndex: 'categoria_nombre',
      title: 'Categoría',
    },
    {
      dataIndex: 'requerido',
      title: 'Acciones',
      align: 'center',
      render: (_t, record) => (
        <Switch
          value={required[record.value]}
          onChange={(c) => onSwitch(record.value, c)}
          checkedChildren="Obligatorio"
          unCheckedChildren="Opcional"
          disabled={record.obligatorio}
        />
      ),
    },
  ];

  const onSave = async () => {
    try {
      setLoading(true);
      await setTemplates(project_id, {
        tipo_contrato: params.tipo_contrato,
        tipo_persona: params.tipo_persona,
        tipos: selected.map((s) => ({
          tipo_plantilla: s,
          requerido: required[s],
        })),
      });

      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
    } catch (err) {
      catchExceptionSRate(err);
    } finally {
      setLoading(false);
    }
  };

  if (!dataSource) {
    return <Skeleton active loading />;
  }

  return (
    <>
      <Transfer
        targetKeys={selected}
        showSearch
        showSelectAll={false}
        filterOption={(inputValue, item) =>
          item.label.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
        }
        rowKey={(record) => record.value}
        onChange={setSelected}
        dataSource={dataSource}
      >
        {({
          direction,
          filteredItems,
          selectedKeys: listSelectedKeys,
          onItemSelectAll,
        }) => {
          const rowSelection = {
            renderCell: (_checked, record, _index, originNode) => {
              if (record.obligatorio && direction === 'right') return null;
              return originNode;
            },
            onChange(selectedRowKeys, selectedRows) {
              if (direction === 'right')
                onItemSelectAll(
                  selectedRows
                    .filter((i) => !i.obligatorio)
                    .map((i) => i.value),
                  'replace',
                );
              else onItemSelectAll(selectedRowKeys, 'replace');
            },
            selectedRowKeys: listSelectedKeys,
          };
          return (
            <Table
              rowKey="value"
              rowSelection={rowSelection}
              dataSource={filteredItems}
              columns={direction === 'right' ? columnsRight : columns}
              pagination={false}
            />
          );
        }}
      </Transfer>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          margin: '1em',
        }}
      >
        <CustomButton text="Guardar" loading={loading} onClick={onSave} />
      </div>
    </>
  );
};

const ProjectTemplateForms = ({
  project_id,
  process_types,
  contract_types,
  disabled,
}) => {
  const [processType, setProcessType] = useState(disabled ? undefined : 1);

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '1em',
        }}
      >
        <Segmented
          value={processType}
          onChange={setProcessType}
          options={process_types.map((s) => ({
            value: s.value,
            label: s.label,
            disabled: disabled || (s.selected ? false : true),
          }))}
        />
      </div>
      <Collapse
        accordion
        items={(contract_types[processType] || []).map((s) => ({
          key: s.value,
          label: s.label,
          collapsible: s.selected && !disabled ? undefined : 'disabled',
          children: !disabled ? (
            <Tabs
              type="card"
              items={[
                {
                  key: 1,
                  label: 'Persona natural',
                  children: (
                    <ProjectContractTypeTemplateInput
                      project_id={project_id}
                      params={{
                        tipo_contrato: s.value,
                        tipo_persona: 1,
                      }}
                    />
                  ),
                },
                {
                  key: 2,
                  label: 'Persona jurídica',
                  children: (
                    <ProjectContractTypeTemplateInput
                      project_id={project_id}
                      params={{
                        tipo_contrato: s.value,
                        tipo_persona: 2,
                      }}
                    />
                  ),
                },
              ]}
            />
          ) : null,
        }))}
      />
    </>
  );
};

const ProjectsForms = ({ history, match, readOnly = false }) => {
  let query = useQuery();
  const closed_type = query.get('closed_type');
  const [projectID, setProjectID] = useState(parseInt(match.params.project_id));
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('Creación de Proyecto');
  const [initialValues, setInitialValues] = useState(null);
  const [clients, setClients] = useState(null);
  const [organizations, setOrganizations] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [projectCategoryID, setProjectCategoryID] = useState(0);
  const [loadingProjectCategory, setLoadingProjectCategory] = useState(false);
  const [formProjectCategory] = Form.useForm();
  const [form] = Form.useForm();
  const [tipoProyecto, setTipoProyecto] = useState(0);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [pendingChanges, setPendingChanges] = useState(false);
  const [templateInfo, setTemplateInfo] = useState(undefined);

  const columns = [
    {
      title: 'Proyecto',
      dataIndex: 'nombre_proyecto',
      key: 'nombre_proyecto',
    },
    {
      title: 'Categoría',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Acciones',
      dataIndex: 'Acciones',
      align: 'center',
      fixed: 'right',
      render: (_, record) =>
        dataSource.length >= 1 && !readOnly ? (
          <Space>
            <CustomButton
              type="default"
              text="Editar"
              htmlType={'button'}
              className={'ant-btn-info'}
              onClick={() => {
                editProjectCategory(record);
              }}
            />
            <CustomButton
              danger
              type={'default'}
              htmlType={'button'}
              loading={loadingDelete}
              text={
                <Popconfirm
                  title={MESSAGES.DELETE_CONFIRM_MSG}
                  onConfirm={() => deleteDataSourceProjectCategory(record.id)}
                >
                  Eliminar
                </Popconfirm>
              }
            />
          </Space>
        ) : null,
    },
  ];
  const save = async (data) => {
    setLoading(true);
    try {
      let response;
      if (!projectID) {
        response = await postProject(data);
      } else {
        response = await putProject(projectID, data);
      }
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      history.push(`${INTERNAL_LINKS.PROJECT}/${response.data.id}`);
      setProjectID(response.data.id);
      setLoading(false);
    } catch (err) {
      catchExceptionSRate(err, MESSAGES.DESC_ERROR_POST_MSG);
      setLoading(false);
    }
  };

  // Get Data by ID
  const handleGetData = async () => {
    try {
      setLoading(true);
      if (projectID) {
        const response = await getProject(projectID);
        if (response.data['fecha_inicio'] != null) {
          response.data['fecha_inicio'] = dayjs(response.data.fecha_inicio);
        }
        if (response.data['fecha_fin'] != null) {
          response.data['fecha_fin'] = dayjs(response.data.fecha_fin);
        }
        setLoading(false);
        setInitialValues(response.data);
        setTemplateInfo({
          process_types: response.data.process_types.map((s) => ({
            ...s,
            selected: response.data.tipos_proceso.includes(s.value),
          })),
          contract_types: response.data.contract_types.reduce((pv, cv) => {
            const key = cv.tipo_contratacion;
            (pv[key] ??= []).push({
              ...cv,
              selected: response.data.tipos_contrato.includes(cv.value),
            });
            return pv;
          }, {}),
        });
        setTitle('Actualización de Proyecto');
        setTipoProyecto(response.data.tipo_proyecto);
      } else {
        const response = await projectInfo();
        setTemplateInfo({
          process_types: response.data.process_types.map((s) => ({
            ...s,
            selected: s.value === 1,
          })),
          contract_types: response.data.contract_types.reduce((pv, cv) => {
            const key = cv.tipo_contratacion;
            (pv[key] ??= []).push({
              ...cv,
              selected: cv.value === 1,
            });
            return pv;
          }, {}),
        });
        setLoading(false);
        setInitialValues({
          tipo_moneda: 1,
          tipos_proceso: [1],
          tipos_contrato: [1],
        });
      }
    } catch (err) {
      catchExceptionSRate(err);
      setLoading(false);
    }
  };

  const handleGetClients = async () => {
    try {
      const response = await getAllClients();
      setClients(response.data.results);
    } catch (err) {
      catchExceptionSRate(err);
    }
  };

  const handleGetOrganizations = async () => {
    try {
      const response = await getAllOrganizations();
      setOrganizations(response.data.results);
    } catch (err) {
      catchExceptionSRate(err);
    }
  };

  const getDataSourceProjectCategory = async () => {
    try {
      if (projectID) {
        const res = await getAllProjectCategory(projectID);
        setDataSource(res.data.results);
      }
    } catch (err) {
      catchExceptionSRate(err);
    }
  };

  const saveProjectCategory = async (data) => {
    try {
      let response;

      setLoadingProjectCategory(true);
      if (projectCategoryID === 0) {
        data['proyecto'] = projectID;
        response = await postAllProjectCategory(data);
      } else {
        response = await putAllProjectCategory(projectCategoryID, data);
      }
      setLoadingProjectCategory(false);

      if ([200, 201, 204].indexOf(response.status) > -1) {
        formProjectCategory.resetFields();
        setProjectCategoryID(0);
        await getDataSourceProjectCategory();
        notification.success({
          message: MESSAGES.SUCCESS_MSG,
          description: MESSAGES.DESC_SUCCESS_MSG,
          duration: 2,
        });
      }
    } catch (err) {
      catchExceptionSRate(err, MESSAGES.DESC_ERROR_POST_MSG);
      setLoadingProjectCategory(false);
    }
  };

  const editProjectCategory = (data) => {
    formProjectCategory.setFieldsValue(data);
    setProjectCategoryID(data.id);
  };

  // Delete Object by Id of List
  const deleteDataSourceProjectCategory = useCallback(
    async (id) => {
      try {
        setLoadingDelete(true);
        const response = await remove(id);
        setLoadingDelete(false);
        if ([200, 201, 204].indexOf(response.status) > -1) {
          notification.success({
            message: MESSAGES.SUCCESS_MSG,
            description: MESSAGES.DESC_SUCCESS_DELETE_MSG,
            duration: 2,
          });
          setDataSource(dataSource.filter((item) => item.id !== id));
        }
      } catch (err) {
        catchExceptionSRate(err, MESSAGES.DESC_ERROR_DELETE_MSG);
      }
    },
    [dataSource],
  );

  useEffect(() => {
    handleGetData();
    handleGetClients();
    handleGetOrganizations();
    getDataSourceProjectCategory();
  }, []);

  return (
    <Skeleton loading={loading}>
      {initialValues != null && (
        <>
          <ProjectForm
            title={title}
            onFinish={save}
            initialValues={initialValues}
            loading={loading}
            clients={clients}
            organizations={organizations}
            project_id={projectID}
            form={form}
            readOnly={readOnly}
            templateInfo={templateInfo}
            setPendingChanges={setPendingChanges}
          />
          {!readOnly && projectID ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CustomButton
                text="Guardar"
                loading={loading}
                onClick={() => form.submit()}
                style={{ marginBottom: '1em' }}
                htmlType="button"
              />
            </div>
          ) : (
            <Row gutter={12}>
              <Col span={24}>
                <Space>
                  <CustomButton
                    text="Guardar"
                    loading={loading}
                    onClick={() => form.submit()}
                    htmlType="button"
                  />
                  <CustomButton
                    type={'default'}
                    className={'back-button'}
                    htmlType="button"
                    text={<Link to={INTERNAL_LINKS.PROJECTS}>Volver</Link>}
                  />
                </Space>
              </Col>
            </Row>
          )}
        </>
      )}
      {projectID > 0 && !!templateInfo && (
        <>
          <Card
            size="small"
            bordered={false}
            title={<Title level={3}>Plantillas a usar en contratación</Title>}
          >
            {pendingChanges ? (
              <Alert
                type="warning"
                showIcon
                description="Por favor guarde antes de seleccionar plantillas"
              />
            ) : null}

            <ProjectTemplateForms
              project_id={projectID}
              process_types={templateInfo.process_types}
              contract_types={templateInfo.contract_types}
              disabled={readOnly || loading || pendingChanges}
            />
          </Card>
        </>
      )}
      {projectID > 0 && tipoProyecto === 0 && (
        <Content>
          {!readOnly && (
            <Form
              form={formProjectCategory}
              name="nest-messages"
              layout="vertical"
              autoComplete="off"
              onFinish={(data) => {
                saveProjectCategory(data);
              }}
            >
              <Card
                size={'small'}
                bordered={false}
                title={<Title level={3}>Subcategorías del proyecto</Title>}
              >
                <Row gutter={12}>
                  <Col span={24}>
                    <Form.Item
                      name="nombre"
                      label="Agregar pruebas o subcategorías del proyecto"
                      rules={[
                        {
                          required: true,
                        },
                        () => ({
                          validator(_, value) {
                            if (!value || value.length <= 100) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              'Solo puede ingresar 100 caracteres!',
                            );
                          },
                        }),
                      ]}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={24}>
                    <Form.Item>
                      <Space>
                        <CustomButton
                          loading={loadingProjectCategory}
                          text="Agregar"
                        />
                        {projectCategoryID !== 0 && (
                          <CustomButton
                            danger
                            type={'default'}
                            htmlType={'button'}
                            onClick={() => {
                              setProjectCategoryID(0);
                              formProjectCategory.resetFields();
                            }}
                            text="Cancelar edición"
                          />
                        )}
                      </Space>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Form>
          )}
          <SpinLoad loading={loadingProjectCategory}>
            <Table
              rowKey="id"
              scroll={{ x: 400 }}
              dataSource={dataSource}
              columns={columns}
              title={() => (
                <Paragraph className={'margin-5'} strong>
                  Listado de Subcategorías del proyecto
                </Paragraph>
              )}
            />
          </SpinLoad>
        </Content>
      )}
      {projectID > 0 && (
        <Content>
          <Card
            size={'small'}
            bordered={false}
            title={<Title level={3}>Estado del proyecto</Title>}
          >
            {initialValues && initialValues.estados_proyecto && (
              <List
                dataSource={initialValues.estados_proyecto}
                renderItem={(item) => (
                  <List.Item>
                    <Text>
                      <Text code>
                        {dayjs(item.created_at).format('YYYY-MM-DD h:mm a')}
                      </Text>{' '}
                      {item.estado_descripcion}
                      {' | '}
                      {item.usuario_nombre}
                    </Text>
                  </List.Item>
                )}
              />
            )}
          </Card>
          <Row gutter={12} style={{ marginTop: 10 }}>
            <Col span={24}>
              <CustomButton
                type={'default'}
                className={'back-button'}
                htmlType="button"
                text={
                  <Link
                    to={{
                      pathname: INTERNAL_LINKS.PROJECTS,
                      state: { closed_type: closed_type },
                    }}
                  >
                    Volver
                  </Link>
                }
              />
            </Col>
          </Row>
        </Content>
      )}
    </Skeleton>
  );
};

export default ProjectsForms;
